import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCheckCircleOutline, mdiCloseCircleOutline } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useDynamicRefresh } from '../../hooks/useDynamicRefresh'
import { AgentRunsPanel } from '../agent-runs/AgentRunsPanel'
import { useLocale } from '../../contexts/LocaleContext'
import { useLiveSubscription, useScopeLive } from '../../contexts/LiveEventsContext'
import { Skeleton } from '../ui/skeleton'
import { Alert, AlertDescription } from '../ui/alert'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { ImportDepositZone } from './ImportDepositZone'
import { ImportStatusBadge } from './ImportsListPage'
import { ImportFunnelRibbon } from './ImportFunnelRibbon'
import { ImportEntityCards } from './ImportEntityCards'
import { ImportDocumentTypeBars } from './ImportDocumentTypeBars'
import { ImportGapsPanel } from './ImportGapsPanel'
import { ImportOntologyDriftAlert } from '../ontology/OntologyDriftNotice'
import { ImportDepositTree } from './ImportDepositTree'
import { ImportReviewCard } from './ImportReviewCard'
import { ImportBlockingApproval } from './ImportBlockingApproval'
import { ImportProposalsReview } from './ImportProposalsReview'
import { ImportDraftLeasesReview } from './ImportDraftLeasesReview'
import { ImportExtractionStep } from './ImportExtractionStep'
import { ImportAIUsageStat } from './ImportAIUsageStat'
import { ImportIngestionTimeStat } from './ImportIngestionTimeStat'
import { ImportProvenanceFeed } from './ImportProvenanceFeed'
import { normalizeSummary } from './importTypes'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * The import dashboard (spec §7): header, funnel ribbon, entity cards,
 * document-types | gaps row, provenance feed.
 *
 * Live mode (spec §5): while the import is `running` the summary is polled
 * through the standard dynamic-refresh hook (fast while numbers move, backing
 * off to 30s when they stop). Polling stops on its own the moment the import
 * reaches a terminal status — the summary is frozen server-side at that point,
 * so there is nothing left to observe.
 */
export function ImportDashboardPage() {
    const { org, importUid } = useParams({ strict: false }) as { org: string; importUid: string }
    const { t } = useTranslation('imports')
    const { locale } = useLocale()

    const { data, isLoading, error, refetch } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/imports/{importUid}/summary',
        { params: { path: { orgCode: org, importUid } } },
    )

    const summary = useMemo(() => normalizeSummary(data), [data])
    const isRunning = summary?.import.status === 'running'

    // Status actions (spec 2026-08-16-05 §1): closing a deposit stays a manual
    // gesture, so both transitions are behind an AlertDialog confirmation.
    const [pendingStatus, setPendingStatus] = useState<'completed' | 'cancelled' | null>(null)
    const [statusError, setStatusError] = useState<string | null>(null)
    const statusMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/imports/{importUid}')

    const pendingApproval = summary?.pending_approval ?? null
    const hasPendingApproval = !!pendingApproval && (pendingApproval.assets > 0 || pendingApproval.documents > 0)

    // Approve everything the import created that is still to approve, in one
    // gesture (POST /imports/{uid}/approve).
    const approveImport = $api.useMutation('post', '/api/v1/organizations/{orgCode}/imports/{importUid}/approve')
    const [approveError, setApproveError] = useState(false)
    const approveAll = async () => {
        setApproveError(false)
        try {
            await approveImport.mutateAsync({ params: { path: { orgCode: org, importUid } } })
            await refetch()
        } catch {
            setApproveError(true)
        }
    }

    const applyStatus = async (status: 'completed' | 'cancelled') => {
        setStatusError(null)
        try {
            await statusMutation.mutateAsync({
                params: { path: { orgCode: org, importUid } },
                body: { status },
            })
            await refetch()
        } catch {
            setStatusError(t('actions.error'))
        } finally {
            setPendingStatus(null)
        }
    }

    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('common:nav.home'), path: `/${org}` },
                { title: t('breadcrumb'), path: `/${org}/imports` },
                { title: summary?.import.label ?? importUid },
            ],
            [org, t, summary?.import.label, importUid],
        ),
    )

    // Live mode (spec 2026-09-30-11): the summary aggregates documents, jobs,
    // assets, contracts and users, so the page subscribes to those scopes and
    // live-invalidation.ts refetches the summary on each (hub-coalesced) hint.
    // While the socket is acked, polling is pinned to the 5 min safety net.
    const liveEnabled = !!isRunning
    useLiveSubscription({ entity: 'documents' }, { enabled: liveEnabled })
    useLiveSubscription({ entity: 'jobs' }, { enabled: liveEnabled })
    useLiveSubscription({ entity: 'assets' }, { enabled: liveEnabled })
    useLiveSubscription({ entity: 'contracts' }, { enabled: liveEnabled })
    useLiveSubscription({ entity: 'users' }, { enabled: liveEnabled })
    const live = useScopeLive({ entity: 'documents' }) && liveEnabled

    const dynamicRefresh = useDynamicRefresh({
        enabled: !!isRunning,
        defaultInterval: 2000,
        live,
    })

    // Standard dynamic-refresh wiring (docs/frontend/dynamic-refresh.md):
    // refetch when the countdown hits zero, then feed the freshest server
    // timestamp back so the interval speeds up while the deposit is moving and
    // relaxes when it settles.
    useEffect(() => {
        if (!dynamicRefresh.isEnabled || !isRunning) return
        if (dynamicRefresh.timeRemaining !== 0) return

        refetch().then(({ data: fresh }) => {
            const next = normalizeSummary(fresh)
            if (!next) return
            const stamp = next.computed_at ?? ''
            dynamicRefresh.controls.adjustRate(stamp !== dynamicRefresh.lastTimestamp, stamp)
        })
        // dynamicRefresh is a stable-ish object; keying on timeRemaining is the
        // documented trigger for this hook.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicRefresh.timeRemaining, dynamicRefresh.isEnabled, isRunning])

    if (isLoading) {
        return (
            <div className="space-y-4" data-testid="import-dashboard-loading">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-40 w-full" />
            </div>
        )
    }

    if (error || !summary) {
        return (
            <div data-testid="import-dashboard-error">
                <Alert variant="destructive">
                    <AlertDescription>{t('detail.error')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    const started = new Date(summary.import.started_at).toLocaleString(locale)

    return (
        <div className="space-y-8" data-testid="import-dashboard">
            <PageHeader
                icon={SECTION_ICONS.imports}
                data-testid="import-label"
                title={summary.import.label}
                description={
                    <>
                        {started}
                        {summary.import.finished_at
                            ? ` → ${new Date(summary.import.finished_at).toLocaleString(locale)}`
                            : ''}
                    </>
                }
                actions={
                <>
                    {isRunning && (
                        <span className="text-xs text-muted-foreground" data-testid="import-live-indicator">
                            {live
                                ? t('detail.liveSocket')
                                : t('detail.live', {
                                      seconds: Math.round(dynamicRefresh.refreshInterval / 1000),
                                  })}
                        </span>
                    )}
                    <ImportStatusBadge status={summary.import.status} />
                    {isRunning && (
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={() => setPendingStatus('completed')}
                                disabled={statusMutation.isPending}
                                aria-label={t('actions.complete')}
                                title={t('actions.complete')}
                                data-testid="complete-import-btn"
                            >
                                <AppIcon path={mdiCheckCircleOutline} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('actions.complete')}</span>
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setPendingStatus('cancelled')}
                                disabled={statusMutation.isPending}
                                aria-label={t('actions.cancel')}
                                title={t('actions.cancel')}
                                data-testid="cancel-import-btn"
                            >
                                <AppIcon path={mdiCloseCircleOutline} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('actions.cancel')}</span>
                            </Button>
                        </div>
                    )}
                </>
                }
            />

            {statusError && (
                <Alert variant="destructive" data-testid="import-status-error">
                    <AlertDescription>{statusError}</AlertDescription>
                </Alert>
            )}

            <AlertDialog open={pendingStatus !== null} onOpenChange={open => { if (!open) setPendingStatus(null) }}>
                <AlertDialogContent data-testid="import-status-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingStatus === 'cancelled' ? t('actions.cancel_title') : t('actions.complete_title')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingStatus === 'cancelled'
                                ? t('actions.cancel_description')
                                : t('actions.complete_description')}
                            {pendingStatus === 'completed' && hasPendingApproval && (
                                <span className="mt-2 block font-medium" data-testid="import-status-confirm-pending">
                                    {t('actions.complete_pending_approval', {
                                        assets: pendingApproval.assets,
                                        documents: pendingApproval.documents,
                                    })}
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="import-status-confirm-cancel">
                            {t('common:actions.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => pendingStatus && void applyStatus(pendingStatus)}
                            data-testid="import-status-confirm-ok"
                        >
                            {t('actions.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {hasPendingApproval && (
                <Alert data-testid="import-pending-approval-banner">
                    <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                        <span data-testid="import-pending-approval-text">
                            {t('pendingApproval.text', {
                                assets: pendingApproval.assets,
                                documents: pendingApproval.documents,
                                count: pendingApproval.assets,
                            })}
                        </span>
                        <span className="flex flex-wrap items-center gap-2">
                        {approveError && <span className="text-destructive">{t('actions.error')}</span>}
                        {pendingApproval.assets > 0 && (
                            <Button size="sm" onClick={() => void approveAll()} disabled={approveImport.isPending} data-testid="import-approve-all">
                                {t('pendingApproval.approve_all')}
                            </Button>
                        )}
                        <Button asChild size="sm" variant="outline">
                            <Link
                                to="/$org/assets"
                                params={{ org }}
                                search={{
                                    flag: 'to_approve',
                                    import_uid: importUid,
                                } as never}
                                data-testid="import-pending-approval-link"
                            >
                                {t('pendingApproval.link')}
                            </Link>
                        </Button>
                        </span>
                    </AlertDescription>
                    <ImportBlockingApproval org={org} blocking={pendingApproval.blocking ?? []} />
                </Alert>
            )}

            <ImportOntologyDriftAlert org={org} documentTypeCodes={summary.document_types.map((d) => d.code)} />

            {isRunning && (
                <ImportExtractionStep
                    org={org}
                    importUid={importUid}
                    extractionStartedAt={summary.import.extraction_started_at}
                    extractionTier={summary.import.extraction_tier}
                    awaitingExtraction={summary.import.awaiting_extraction ?? 0}
                    onChange={() => void refetch()}
                />
            )}

            {isRunning && <ImportDepositZone org={org} importUid={importUid} />}

            <ImportFunnelRibbon org={org} importUid={importUid} funnel={summary.funnel} />

            {/*
              Deposit tree and review queue (spec 2026-08-16-07 §2). Both ride
              on the same summary payload, so they follow the page's existing
              live/frozen behavior for free. They are hidden — rather than shown
              empty — on a completed import whose frozen blob predates them.
            */}
            {(isRunning || summary.folder_tree.length > 0) && (
                <ImportDepositTree
                    org={org}
                    importUid={importUid}
                    tree={summary.folder_tree}
                    truncated={summary.folder_tree_truncated}
                    omittedFolders={summary.folder_tree_omitted_folders}
                    omittedDocuments={summary.folder_tree_omitted_documents}
                />
            )}

            {summary.review && (
                <ImportReviewCard org={org} importUid={importUid} review={summary.review} />
            )}

            <AgentRunsPanel org={org} targetEntityType="import" targetUid={importUid} missionTarget="import" />

            <ImportProposalsReview org={org} importUid={importUid} />

            <ImportDraftLeasesReview org={org} importUid={importUid} />

            {/*
              What the deposit cost in paid AI calls (spec 2026-08-16-08 §3).
              Hidden — not zeroed — when the frozen blob predates the rollup:
              "$0.00" would read as "this deposit was free", which is a
              different claim from "we did not measure it".
            */}
            {summary.ai_usage && <ImportAIUsageStat usage={summary.ai_usage} />}

            {/* Not measured (an import older than the ingestion timing) hides the line. */}
            {summary.timing && <ImportIngestionTimeStat timing={summary.timing} />}

            <ImportEntityCards org={org} importUid={importUid} entities={summary.entities} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <ImportDocumentTypeBars
                    org={org}
                    importUid={importUid}
                    documentTypes={summary.document_types}
                />
                <ImportGapsPanel org={org} importUid={importUid} gaps={summary.gaps} />
            </div>

            <ImportProvenanceFeed org={org} rows={summary.recent_values} />
        </div>
    )
}
